import { useState, useCallback } from 'react';

export interface ModuleProgress {
  openedResources: string[];
  quizPassed: boolean;
}

const DEFAULT_PROGRESS: ModuleProgress = {
  openedResources: [],
  quizPassed: false,
};

function getStorageKey(moduleId: string, userId: string | undefined): string | null {
  return userId ? `progress_${moduleId}_${userId}` : null;
}

function loadProgress(key: string | null): ModuleProgress {
  if (!key) return DEFAULT_PROGRESS;
  try {
    const stored = localStorage.getItem(key);
    return stored ? (JSON.parse(stored) as ModuleProgress) : DEFAULT_PROGRESS;
  } catch {
    return DEFAULT_PROGRESS;
  }
}

export function useModuleProgress(moduleId: string, userId: string | undefined) {
  const storageKey = getStorageKey(moduleId, userId);

  const [progress, setProgress] = useState<ModuleProgress>(() => loadProgress(storageKey));

  const persist = useCallback(
    (updated: ModuleProgress) => {
      if (storageKey) {
        localStorage.setItem(storageKey, JSON.stringify(updated));
      }
      setProgress(updated);
    },
    [storageKey],
  );

  const markResourceOpened = useCallback(
    (resourceId: string) => {
      setProgress((prev) => {
        if (prev.openedResources.includes(resourceId)) return prev;
        const updated: ModuleProgress = {
          ...prev,
          openedResources: [...prev.openedResources, resourceId],
        };
        if (storageKey) localStorage.setItem(storageKey, JSON.stringify(updated));
        return updated;
      });
    },
    [storageKey],
  );

  const markQuizPassed = useCallback(() => {
    setProgress((prev) => {
      if (prev.quizPassed) return prev;
      const updated: ModuleProgress = { ...prev, quizPassed: true };
      if (storageKey) localStorage.setItem(storageKey, JSON.stringify(updated));
      return updated;
    });
  }, [storageKey]);

  return { progress, markResourceOpened, markQuizPassed, persist };
}
